// =====================================================
// SalesDashboard  (Sales Executive Module — Task 3)
//
// The page behind the "Sales Executive" button on the Home screen. Renders the
// three sections the module defines, all from ONE backend call:
//   • Performance  — today's signups, today's valid users, total valid users
//   • Incentives   — today / monthly / pending / approved / paid / pending amount
//   • Referral     — the executive's referral link and referral code
//
// Authorisation is entirely server-side: /api/sales/me/dashboard is role-gated
// against the LIVE database role, so an account whose role was removed or
// suspended lands on the "no access" state below instead of a dashboard. The
// route itself only requires a normal login — no role is ever trusted from the
// client.
// =====================================================

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { salesExecutive, errorMessage } from "../services/salesApi";
import SalesTermsModal from "./SalesTermsModal";
import "./SalesDashboard.css";

const REFRESH_MS = 60 * 1000;

// ── formatting helpers ──
const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function fmtDay(day) {
  if (!day) return "—";
  const d = new Date(`${day}T00:00:00`);
  if (isNaN(d)) return day;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function SalesDashboard() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState("");
  const [copied, setCopied] = useState("");
  // Point 11: the always-accessible read-only T&C view (permanent link).
  const [showTermsView, setShowTermsView] = useState(false);

  // ── LOAD ──
  const load = useCallback(async (showSpinner) => {
    if (showSpinner) setLoading(true);
    try {
      const [dash, inc] = await Promise.all([
        salesExecutive.dashboard(),
        salesExecutive.incentives({ limit: 30 }),
      ]);
      setData(dash);
      setHistory(inc.items || []);
      setError("");
      setDenied("");
    } catch (err) {
      // 403 = not (or no longer) an active Sales Executive. That is not an
      // error to retry — it is the answer, so it gets its own state.
      if (err?.response?.status === 403) {
        setDenied(errorMessage(err, "Sales Executive access required"));
      } else {
        setError(errorMessage(err, "Failed to load your dashboard"));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") load(false);
    }, REFRESH_MS);
    return () => clearInterval(iv);
  }, [load]);

  // ── COPY / SHARE ──
  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard API is unavailable on insecure origins / older mobile
      // browsers — fall back to the legacy selection copy so the button still
      // works everywhere.
      const el = document.createElement("textarea");
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
    }
    setCopied(label);
    setTimeout(() => setCopied(""), 2000);
  };

  const share = async () => {
    const link = data?.referral?.referralLink;
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join TheBlogsSphere",
          text: "Create your account on TheBlogsSphere using my referral link:",
          url: link,
        });
        return;
      } catch {
        // User dismissed the share sheet — fall through to copy.
      }
    }
    copy(link, "link");
  };

  // ── NO ACCESS ──
  if (denied) {
    return (
      <div className="sx-root">
        <Navbar />
        <div className="sx-page">
          <div className="sx-denied">
            <div className="sx-denied-icon">🔒</div>
            <h2>Sales Executive access required</h2>
            <p>{denied}</p>
            <button className="sx-btn sx-btn-primary" onClick={() => navigate("/home")}>
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── LOADING ──
  if (loading && !data) {
    return (
      <div className="sx-root">
        <Navbar />
        <div className="sx-page">
          <div className="sx-loading">
            <div className="sx-spinner" />
            <p>Loading your dashboard…</p>
          </div>
        </div>
      </div>
    );
  }

  const perf = data?.performance || {};
  const inc = data?.incentives || {};
  const ref = data?.referral || {};
  const slabs = data?.slabs || [];
  const suspended = data?.executive?.status === "suspended";

  // The next slab the executive has not reached yet, so the dashboard can show
  // exactly what today's remaining gap to the next payout is.
  const nextSlab = [...slabs]
    .sort((a, b) => a.validUsers - b.validUsers)
    .find((s) => (perf.todayValidUsers || 0) < s.validUsers);

  return (
    <div className="sx-root">
      <Navbar />

      <div className="sx-page">
        {/* ══ HERO ══ */}
        <div className="sx-hero">
          <h1 className="sx-title">📈 Sales Executive Dashboard</h1>
          <p className="sx-sub">
            Welcome {data?.executive?.name} · {fmtDay(data?.day)}
          </p>
          {suspended && (
            <div className="sx-warning">
              ⚠️ Your account is suspended. Contact the admin.
            </div>
          )}
        </div>

        {error && <div className="sx-error">⚠️ {error}</div>}

        {/* ══ PERFORMANCE ══ */}
        <div className="sx-section">
          <h2 className="sx-section-title">🎯 Performance</h2>
          <div className="sx-cards">
            <StatCard
              icon="🆕"
              value={perf.todaySignups}
              label="Today's Signups"
              hint="Accounts created with your link/code today"
            />
            <StatCard
              icon="✅"
              value={perf.todayValidUsers}
              label="Today's Valid Users"
              hint="Referred + logged in + published a blog"
              accent="green"
            />
            <StatCard
              icon="🏆"
              value={perf.totalValidUsers}
              label="Total Valid Users"
              hint="All-time valid users"
              accent="purple"
            />
            <StatCard
              icon="👥"
              value={perf.totalSignups}
              label="Total Signups"
              hint="All-time referred accounts"
            />
          </div>
        </div>

        {/* ══ INCENTIVES ══ */}
        <div className="sx-section">
          <h2 className="sx-section-title">💰 Incentives</h2>
          <div className="sx-cards">
            <StatCard icon="📅" value={rupees(inc.today)} label="Today's Incentive" />
            <StatCard icon="🗓️" value={rupees(inc.monthly)} label="Monthly Incentive" />
            <StatCard icon="⏳" value={rupees(inc.pending)} label="Pending Incentive" accent="amber" />
            <StatCard icon="✔️" value={rupees(inc.approved)} label="Approved Incentive" accent="green" />
            <StatCard icon="💸" value={rupees(inc.paid)} label="Paid Incentive" accent="purple" />
            <StatCard
              icon="🧾"
              value={rupees(inc.pendingAmount)}
              label="Pending Amount"
              hint="Earned but not yet paid out"
              accent="amber"
            />
            <StatCard
              icon="🧳"
              value={rupees(data?.travelAllowance?.today)}
              label="Travel Allowance"
              hint={`₹${data?.travelAllowance?.amount ?? 100} once ${
                data?.travelAllowance?.threshold ?? 5
              }+ valid users today`}
              accent={data?.travelAllowance?.qualified ? "green" : undefined}
            />
          </div>

          {/* ── SLAB PROGRESS ── */}
          <div className="sx-slabs">
            <div className="sx-slabs-head">
              <span>Daily incentive slabs</span>
              {nextSlab ? (
                <span className="sx-slabs-next">
                  {nextSlab.validUsers - (perf.todayValidUsers || 0)} more valid
                  users today → {rupees(nextSlab.amount)}
                </span>
              ) : (
                <span className="sx-slabs-next">Top slab reached today 🎉</span>
              )}
            </div>
            <div className="sx-slab-row">
              {[...slabs]
                .sort((a, b) => a.validUsers - b.validUsers)
                .map((s) => {
                  const reached = (perf.todayValidUsers || 0) >= s.validUsers;
                  return (
                    <div
                      key={s.validUsers}
                      className={`sx-slab ${reached ? "sx-slab-reached" : ""}`}
                    >
                      <span className="sx-slab-users">{s.validUsers} valid users</span>
                      <span className="sx-slab-amount">{rupees(s.amount)}</span>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        {/* ══ REFERRAL ══ */}
        <div className="sx-section">
          <h2 className="sx-section-title">🔗 Referral</h2>
          <div className="sx-referral">
            <div className="sx-ref-field">
              <label>Referral Link</label>
              <div className="sx-ref-row">
                <input className="sx-ref-input" value={ref.referralLink || ""} readOnly />
                <button
                  className="sx-btn sx-btn-primary"
                  onClick={() => copy(ref.referralLink, "link")}
                >
                  {copied === "link" ? "Copied ✓" : "Copy"}
                </button>
                <button className="sx-btn" onClick={share}>
                  Share
                </button>
              </div>
            </div>

            <div className="sx-ref-field">
              <label>Referral Code</label>
              <div className="sx-ref-row">
                <input
                  className="sx-ref-input sx-ref-code"
                  value={ref.referralCode || ""}
                  readOnly
                />
                <button
                  className="sx-btn sx-btn-primary"
                  onClick={() => copy(ref.referralCode, "code")}
                >
                  {copied === "code" ? "Copied ✓" : "Copy"}
                </button>
              </div>
            </div>

            <p className="sx-ref-note">
              A user can join either by opening your referral link, or by
              searching TheBlogsSphere, registering normally and entering your
              referral code on the registration form. Both link the account to
              you automatically.
            </p>
          </div>
        </div>

        {/* ══ INCENTIVE HISTORY ══ */}
        <div className="sx-section">
          <h2 className="sx-section-title">📜 Incentive History</h2>
          <div className="sx-table-wrap">
            <table className="sx-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Valid Users</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="sx-empty">
                      No incentives earned yet. Your first payout unlocks at{" "}
                      {slabs.length
                        ? `${slabs[slabs.length - 1].validUsers} valid users in a day`
                        : "the first slab"}
                      .
                    </td>
                  </tr>
                ) : (
                  history.map((row) => (
                    <tr key={row._id}>
                      <td>{fmtDay(row.day)}</td>
                      <td>{row.validUsers}</td>
                      <td className="sx-amount">{rupees(row.amount)}</td>
                      <td>
                        <span className={`sx-badge sx-badge-${row.status}`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="sx-back">
          <Link to="/home" className="sx-btn">
            ← Back to Home
          </Link>
          {/* Point 11: permanent, always-accessible T&C link. */}
          <button className="sx-btn" onClick={() => setShowTermsView(true)}>
            📜 Terms &amp; Conditions
          </button>
        </div>
      </div>

      {/* Point 11: MANDATORY acceptance gate — shown on first access / after a
          version bump. Blocks the dashboard until scrolled + accepted. */}
      {data?.terms?.acceptanceRequired && (
        <SalesTermsModal mode="gate" onAccepted={() => load(false)} />
      )}

      {/* Point 11: read-only re-read of the T&C at any time. */}
      {showTermsView && (
        <SalesTermsModal mode="view" onClose={() => setShowTermsView(false)} />
      )}
    </div>
  );
}

// ── STAT CARD ──
function StatCard({ icon, value, label, hint, accent }) {
  return (
    <div className={`sx-card ${accent ? `sx-card-${accent}` : ""}`}>
      <span className="sx-card-icon">{icon}</span>
      <div>
        <div className="sx-card-num">{value ?? 0}</div>
        <div className="sx-card-label">{label}</div>
        {hint && <div className="sx-card-hint">{hint}</div>}
      </div>
    </div>
  );
}
